<template>
  <q-toolbar class="justify-between q-my-xs" :class="{ 'q-pl-sm': isAtMobileTitleSize }">
    <div class="logo-title row items-center justify-between no-wrap full-width">
      <div>
        <q-toolbar-title class="title row justify-center items-center no-wrap q-pr-none flex-unset">
          <q-btn borderless dense flat padding="0" :to="{ name: 'map' }">
            <q-spinner v-if="isLoading" />
            <img
              v-else-if="accountImgSrc && !mobileSearchInFocus && !accountImgError && !isLoadingImg"
              class="account-logo"
              data-t="logo"
              :src="accountImgSrc"
              @error="accountImgError = true"
            />
            <div v-else-if="isAtMobileTitleSize && !isIndividual" class="row items-center no-wrap">
              <transition name="horiz-shrink">
                <ZubieLogoSmall v-if="!mobileSearchInFocus" class="logo logo--small" height="32px" title="Zubie" />
              </transition>
            </div>
            <ZubieLogo v-else class="logo" height="32px" title="Zubie" />
          </q-btn>
          <div v-if="!isLoading && appName" class="app-name q-ml-md col-grow zubie-font">
            {{ appName }}
          </div>
          <q-separator
            v-if="isAtMobileTitleSize && appName && !accountImgSrc && $q.screen.gt.md"
            class="q-mx-md q-my-sm bg-white"
            vertical
          />
        </q-toolbar-title>
      </div>
      <div class="col row no-wrap items-center justify-end">
        <div v-if="isAuthenticated && sessionIsReady && !isIndividual" class="full-width row justify-center">
          <UniversalSearch
            ref="universalSearch"
            @blur="searchInFocus = searchInputInFocus"
            @focus="searchInFocus = true"
          />
        </div>
        <transition name="horiz-shrink">
          <div
            v-if="
              $q.platform.is.capacitor && isAuthenticated && sessionIsReady && !isIndividual && !mobileSearchInFocus
            "
            class="row items-center q-ml-xs"
          >
            <MainScannerButton icon="qr_code_scanner" @search="onScanToSearch" />
          </div>
        </transition>
      </div>
    </div>
    <transition name="horiz-shrink">
      <div
        v-if="!mobileSearchInFocus"
        class="row no-wrap items-center"
        :class="{ 'justify-end': !$q.screen.gt.sm || !isAuthenticated }"
      >
        <div class="row items-center">
          <div class="row no-wrap">
            <div>
              <PrimaryButton
                v-if="$q.screen.gt.sm && isAuthenticated"
                aria-label="Contact Support"
                class="intercom-trigger q-px-sm"
                flat
                href="https://help.zubie.com/en/"
                icon="contact_support"
                label="Get Help"
                no-wrap
                square
                target="_blank"
                type="a"
                @click="closeListDialog"
              />
            </div>
            <NotificationsButton v-if="isAuthenticated && canViewNotifications && $q.screen.gt.xs" class="q-ml-xs" />
            <q-btn aria-label="Menu" class="q-ml-sm" dense flat square @click="onMenuClick">
              <NotificationsBadge
                v-if="isAuthenticated && canAccessRoute('/notifications') && !$q.screen.gt.xs && areRecentUnread"
              />
              <q-icon name="menu" />
            </q-btn>
          </div>
        </div>
      </div>
    </transition>
  </q-toolbar>
</template>

<script setup lang="ts">
import { useQuasar } from 'quasar';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import PrimaryButton from 'components/buttons/PrimaryButton.vue';
import NotificationsBadge from 'components/notifications/NotificationsBadge.vue';
import NotificationsButton from 'components/notifications/NotificationsButton.vue';
import UniversalSearch from 'components/universal-search/UniversalSearch.vue';
import ZubieLogoSmall from 'src/assets/zubie-logo-small.svg?component';
import ZubieLogo from 'src/assets/zubie-logo.svg?component';
import { usePageTitle } from 'src/composables/page-title';
import { SEARCH_TYPES } from 'src/services/constants';
import { canAccessRoute } from 'src/services/router';
import MainScannerButton from './MainScannerButton.vue';

defineOptions({ name: 'MainToolbar' });

const store = useStore();
const route = useRoute();
const $q = useQuasar();
const { isAtMobileTitleSize } = usePageTitle();

const searchInFocus = ref(false);
const searchInputInFocus = computed(() => store.state.search.searchInputInFocus);
const mobileSearchInFocus = computed(() => !$q.screen.gt.xs && searchInFocus.value);

const account = computed(() => store.getters['session/account']);
const currentUser = computed(() => store.getters['session/currentUser']);
const hasPermission = computed(() => store.getters['session/hasPermission']);
const isAuthenticated = computed(() => store.getters['session/isAuthenticated']);
const partner = computed(() => store.state.session.partner);
const areRecentUnread = computed(() => store.getters['notifications/areRecentUnread']);

const accountImgError = ref(false);
const isLoadingImg = ref(false);
const isLoading = computed(() => isLoadingImg.value || !sessionIsReady.value);

const accountImgSrc = computed(() => {
  if (!account.value) {
    return '';
  }
  // if (accountValue.logoUrl) {
  //   return fit(accountValue.logoUrl, { height: 36 });
  // }
  if (account.value.key) {
    return `https://res.cloudinary.com/zubie/image/upload/f_png/if_ih_gt_32,h_32/accounts/${account.value.key}/logo`;
  }
  return '';
});

const isAuthRequired = computed(() => route.matched.some((route) => route.meta?.auth));
const sessionIsReady = computed(() => {
  if (route.name === 'loading') {
    return false;
  }
  if (!isAuthRequired.value) {
    return true;
  }
  return isAuthenticated.value;
});

onMounted(() => {
  isLoadingImg.value = isAuthRequired.value;
});

watch([accountImgSrc, sessionIsReady], ([imgSrc, sessionReady]) => {
  preloadAccountLogo(imgSrc, sessionReady);
});

const appName = computed(() => {
  const partnerValue = partner.value as { appName?: string } | null | undefined;
  if (partnerValue?.appName) {
    return partnerValue.appName.replace('Zubie', '');
  }
  return '';
});

const canViewNotifications = computed(() => Boolean(hasPermission.value?.('notificationsView')));
const isIndividual = computed(() => currentUser.value?.isIndividual?.());

function closeListDialog() {
  store.dispatch('map/closeListDialog');
}

function onMenuClick() {
  store.dispatch('app/toggleMainNav');
  closeListDialog();
}

const universalSearch = useTemplateRef('universalSearch');
async function onScanToSearch(terms: string) {
  await nextTick();
  await store.dispatch('search/setSearchType', SEARCH_TYPES.KEYWORD);
  universalSearch.value?.performSearch(terms);
  store.dispatch('search/setSearchedTerms', terms);
}

function preloadAccountLogo(imgSrc: string, sessionReady: boolean) {
  accountImgError.value = false;

  if (!imgSrc || !sessionReady) {
    isLoadingImg.value = false;
    return;
  }

  isLoadingImg.value = true;
  const img = new Image();
  img.onload = () => {
    isLoadingImg.value = false;
  };
  img.onerror = () => {
    isLoadingImg.value = false;
    accountImgError.value = true;
  };
  img.src = imgSrc;
}
</script>

<style lang="scss" scoped>
.title {
  margin-right: 8px;
  max-width: calc(100vw - 314px);
}

.account-logo {
  height: 32px;
  width: 100%;
  display: block;
  object-fit: contain;
}

.logo g {
  height: 100%;
}

.app-name {
  @media (max-width: $breakpoint-md) {
    display: none;
  }
}

:deep(.notification-badge) {
  left: 9px;
  bottom: 7px;
}

.horiz-shrink-enter-active,
.horiz-shrink-leave-active {
  transition:
    max-width 250ms ease,
    opacity 250ms ease,
    padding-left 250ms ease,
    padding-right 250ms ease;
}

/* Hidden state (before enter / after leave) */
.horiz-shrink-enter-from,
.horiz-shrink-leave-to {
  max-width: 0; /* takes no horizontal space */
  opacity: 0;
  padding-left: 0; /* avoid sudden left edge jump */
  padding-right: 0;
}

/* Visible state (after enter / before leave) */
.horiz-shrink-enter-to,
.horiz-shrink-leave-from {
  max-width: 54px;
  opacity: 1;
}
</style>

<style lang="scss">
.mobile-notch-left .q-toolbar {
  padding-left: 0;
}

.mobile-notch-right .q-toolbar {
  padding-right: 0;
}
</style>
